/**
 * 岗位详情页:展示岗位信息 + 能力清单 + 简历筛选
 *
 * 一站完成 ②③:HR 在这里上传简历、看匹配分、点 "发起面试" 跳到创建面试
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReportView } from '../components/ReportView';
import { ScreeningDetail } from '../components/ScreeningDetail';
import { ErrorBoundary } from '../components/ErrorBoundary';
import {
  createInterview, getJob, listScreeningsForJob,
  rerunScreening, uploadAndScreen,
} from '../api/interview';
import type { Job, ScreeningResult } from '../api/types';

export default function AdminJobDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [screenings, setScreenings] = useState<ScreeningResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // 发起面试需要后端 70-120s 生成面试剧本,期间禁用按钮 + 显示进度
  const [creatingInterviewFor, setCreatingInterviewFor] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // silent refresh —— 不切整页 loading,只刷新数据(避免上传后整页闪"加载中")
  const silentRefresh = async () => {
    if (!id) return;
    try {
      const [j, ss] = await Promise.all([getJob(id), listScreeningsForJob(id)]);
      setJob(j);
      setScreenings(ss);
      setError(null);
    } catch (e: any) { setError(e.message); }
  };

  // 仅首次挂载用,显示整页"加载中"
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [j, ss] = await Promise.all([getJob(id), listScreeningsForJob(id)]);
        if (cancelled) return;
        setJob(j);
        setScreenings(ss);
        setError(null);
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const handleUpload = async (file: File) => {
    if (!id) return;
    try {
      setUploading(true);
      await uploadAndScreen(id, file);
      await silentRefresh();
    } catch (e: any) { alert('上传/筛选失败: ' + e.message); }
    finally { setUploading(false); }
  };

  const handleRerun = async (candidateId: string) => {
    if (!id) return;
    try { await rerunScreening(id, candidateId); await silentRefresh(); }
    catch (e: any) { alert(e.message); }
  };

  const handleCreateInterview = async (candidateId: string) => {
    if (!id) return;
    if (creatingInterviewFor) return;
    setCreatingInterviewFor(candidateId);
    try {
      // 后端改成异步:创建立刻返回(< 1s),plan + question_pool 后台生成
      const iv = await createInterview({
        candidate_id: candidateId,
        job_id: id,
        scheduled_at: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
        duration_minutes: 30,
      });
      const url = `${window.location.origin}/interview/${iv.access_token}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      // 立刻跳详情页;详情页会轮询 plan 生成进度
      nav(`/admin/interview/${iv.id}?just_created=1&candidate_link=${encodeURIComponent(url)}`);
    } catch (e: any) {
      alert('发起面试失败: ' + e.message);
    } finally {
      setCreatingInterviewFor(null);
    }
  };

  if (loading) return <div className="container"><p className="hint">加载中...</p></div>;
  if (error) return <div className="container"><div className="banner error">{error}</div></div>;
  if (!job) return null;

  const caps = (job.capabilities || []).filter((c) => c.selected);
  const mustCount = caps.filter((c) => c.priority === 'must_have').length;
  // 新岗位走 capability_model(纲+目),已经在 ReportView ③ 段把 sub_capabilities 展开了;
  // 老岗位没有 capability_model → 兜底展示扁平能力清单。
  const hasCapModel = !!(job.analysis_report?.capability_model && job.analysis_report.capability_model.length > 0);

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1 className="title">{job.title}</h1>
        </div>
        <div className="actions">
          <button className="ghost" onClick={() => nav('/')}>← 返回列表</button>
        </div>
      </div>

      {/* 岗位描述 + 能力 */}
      <div className="card">
        <div className="hint" style={{ marginBottom: 4 }}>岗位描述</div>
        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{job.description}</p>
      </div>

      {/* 深度分析报告(创建时已生成) */}
      {job.analysis_report && Object.keys(job.analysis_report).length > 0 && (
        <ErrorBoundary label="岗位深度分析报告">
          <ReportView report={job.analysis_report} />
        </ErrorBoundary>
      )}

      {/* 老岗位(无 capability_model)兜底:展示扁平能力清单。
          新岗位的 sub_capabilities 已经在 ReportView ③ 段按维度展开,这里不再重复。 */}
      {!hasCapModel && (
        <div className="card">
          <div className="section-title">
            <div className="name">考核能力 <span className="count">({caps.length} 条 · {mustCount} 必备 + {caps.length - mustCount} 加分)</span></div>
          </div>
          {caps.length === 0 ? (
            <p className="hint">未设置 — 创建岗位时未生成或全部取消勾选</p>
          ) : (
            <div className="col" style={{ gap: 6 }}>
              {caps.map((c, i) => (
                <div key={i} className="row" style={{ padding: '8px 12px', background: 'var(--bg-elev)', borderRadius: 6 }}>
                  <span className="tag" style={{
                    background: c.priority === 'must_have' ? 'var(--danger-soft)' : 'var(--surface-2)',
                    color: c.priority === 'must_have' ? '#b91c1c' : 'var(--text-muted)',
                    fontSize: 11, marginRight: 8,
                  }}>
                    {c.priority === 'must_have' ? '★ 必备' : '加分'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{c.name}</div>
                    {c.description && <div className="hint" style={{ marginTop: 2 }}>{c.description}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 简历筛选 */}
      <div className="card">
        <div className="section-title">
          <div className="name">📄 候选人简历筛选 <span className="count">({screenings.length})</span></div>
          <div>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.png,.jpg,.jpeg"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleUpload(f);
                e.target.value = '';
              }}
            />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? '解析中...' : '+ 上传简历'}
            </button>
          </div>
        </div>

        {screenings.length === 0 ? (
          <div className="empty">
            <div className="icon">📄</div>
            <div>还没有筛选过简历</div>
            <div className="hint" style={{ marginTop: 6 }}>上传 PDF/DOCX,LLM 解析 + 按能力清单评分</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>候选人</th>
                <th>匹配度</th>
                <th>推荐</th>
                <th>核心点评</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {screenings.map((s) => {
                const score = s.match_score;
                const isExpanded = expandedId === s.id;
                const hasDeepReport = !!(s.persona_fit || s.capability_dimension_scores?.length);
                return (
                  <Fragment key={s.id}>
                  <tr style={{ cursor: hasDeepReport ? 'pointer' : 'default' }}
                      onClick={() => hasDeepReport && setExpandedId(isExpanded ? null : s.id)}>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {hasDeepReport && <span style={{ marginRight: 4, color: 'var(--text-muted)' }}>{isExpanded ? '▼' : '▶'}</span>}
                        {s.candidate.name || '(未命名)'}
                      </div>
                      <div className="muted" style={{ fontSize: 12 }}>{s.candidate.email}</div>
                    </td>
                    <td>
                      <span className={`score ${matchColor(score)}`}>
                        <span className="num">{score == null ? '—' : Math.round(score)}</span>
                        {score != null && <span className="max">/100</span>}
                      </span>
                    </td>
                    <td>
                      {s.recommendation === 'interview' && <span className="rec yes">建议面试</span>}
                      {s.recommendation === 'pass' && <span className="rec no">不推荐</span>}
                      {!s.recommendation && <span className="rec pending">—</span>}
                    </td>
                    <td className="muted" style={{ fontSize: 13, maxWidth: 360 }}>
                      {(s.match_summary || '').slice(0, 100)}{(s.match_summary || '').length > 100 ? '...' : ''}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row" style={{ gap: 6 }}>
                        {s.recommendation === 'interview' && (
                          <button
                            className="icon"
                            onClick={() => handleCreateInterview(s.candidate.id)}
                            disabled={creatingInterviewFor !== null}
                            title={creatingInterviewFor === s.candidate.id ? '正在生成面试剧本(70-120s)' : '生成面试剧本 + 准备面试间'}
                          >
                            {creatingInterviewFor === s.candidate.id
                              ? '⏳ 生成剧本中...'
                              : creatingInterviewFor !== null
                                ? '▶ 发起面试'
                                : '▶ 发起面试'}
                          </button>
                        )}
                        <button className="ghost icon" onClick={() => handleRerun(s.candidate.id)}>↻ 重筛</button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && hasDeepReport && (
                    <tr>
                      <td colSpan={5} style={{ padding: 0, background: 'var(--bg-elev)' }}>
                        <ErrorBoundary label="候选人匹配诊断">
                          <ScreeningDetail s={s} />
                        </ErrorBoundary>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function matchColor(s: number | null | undefined): string {
  if (s == null) return 'none';
  if (s >= 80) return 'high';
  if (s >= 60) return 'mid';
  return 'low';
}
